import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, ArrowUpRight, Video } from "lucide-react";
import { getCalApi } from "@calcom/embed-react";
import { api } from "@/lib/api";

export default function Booking() {
    const [calLink, setCalLink] = useState("growwithbabur/strategy-call");

    useEffect(() => {
        api.get("/config").then((r) => {
            if (r.data.cal_link) setCalLink(r.data.cal_link);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        (async () => {
            const cal = await getCalApi();
            cal("ui", {
                theme: "dark",
                styles: { branding: { brandColor: "#0090FF" } },
                hideEventTypeDetails: false,
                layout: "month_view",
            });
        })();
    }, []);

    return (
        <section id="book" className="relative py-24 sm:py-32 border-t border-white/10 bg-[#0d0d0f] overflow-hidden">
            <div className="pointer-events-none absolute -top-40 right-0 w-[600px] h-[600px] rounded-full bg-brand/15 blur-[180px]" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <div className="eyebrow mb-4 flex items-center gap-3">
                            <span className="inline-block w-10 h-[2px] bg-brand" /> Free strategy call
                        </div>
                        <h2 className="font-display font-medium tracking-[-0.04em] text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02]">
                            30 minutes.<br />
                            <span className="text-brand">No pitch.</span>
                        </h2>
                        <p className="mt-6 text-white/70 leading-relaxed max-w-md">
                            We audit your current presence, map what's working, tell you exactly what to fix. If we're a fit, we'll talk packages. If not, you leave with a clear plan.
                        </p>

                        <div className="mt-8 space-y-3">
                            {[
                                { t: "Brand audit & competitor snapshot" },
                                { t: "Content gaps that are costing you deals" },
                                { t: "Lead-gen plan tuned to your niche" },
                                { t: "Recommended budget & realistic timeline" },
                            ].map((item, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.2 + i * 0.08, duration: 0.5 }}
                                    className="flex items-center gap-3 text-white/85"
                                >
                                    <span className="font-mono text-brand text-xs">0{i + 1}</span>
                                    <span className="text-sm">{item.t}</span>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-col sm:flex-row gap-3">
                            <button
                                data-cal-link={calLink}
                                data-cal-namespace=""
                                data-cal-config='{"layout":"month_view","theme":"dark"}'
                                data-testid="cal-book-btn"
                                className="group inline-flex items-center justify-between gap-3 bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-4 transition-colors"
                            >
                                <span className="flex items-center gap-2">
                                    <CalendarClock className="w-5 h-5" /> Pick a time
                                </span>
                                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </button>
                            <a
                                href="https://wa.me/971543676278?text=Hi%20Babur!%20I%20want%20to%20book%20a%20strategy%20call."
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/70 text-white font-semibold px-6 py-4 transition-colors"
                            >
                                Prefer WhatsApp?
                            </a>
                        </div>
                    </motion.div>

                    {/* Right: Cal.com visual card */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <div className="relative bg-[#141414] border border-white/10 p-8 sm:p-10">
                            <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-brand/20 blur-3xl" />
                            <div className="relative">
                                <div className="flex items-center justify-between mb-8">
                                    <div className="w-12 h-12 bg-brand/15 border border-brand/30 flex items-center justify-center">
                                        <Video className="w-5 h-5 text-brand" />
                                    </div>
                                    <div className="text-xs font-mono uppercase tracking-widest text-white/40">Zoom / Google Meet</div>
                                </div>

                                <div className="font-display font-semibold text-3xl sm:text-4xl text-white tracking-[-0.03em]">
                                    Strategy Call
                                </div>
                                <div className="mt-2 text-white/60 text-sm">30 minutes &middot; Free &middot; Book any weekday</div>

                                <div className="mt-8 grid grid-cols-3 gap-3 max-w-sm">
                                    {["Mon", "Wed", "Fri"].map((d) => (
                                        <div key={d} className="bg-black/40 border border-white/10 p-3 text-center">
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-brand">{d}</div>
                                            <div className="text-white font-semibold mt-1">9AM–6PM</div>
                                            <div className="text-[10px] text-white/40 mt-1">GST · UAE</div>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    data-cal-link={calLink}
                                    data-cal-config='{"layout":"month_view","theme":"dark"}'
                                    className="mt-10 w-full inline-flex items-center justify-between gap-3 bg-white text-black hover:bg-brand hover:text-white font-semibold px-6 py-4 transition-colors group"
                                >
                                    <span>Open booking calendar</span>
                                    <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                </button>

                                <div className="mt-4 text-center text-[11px] font-mono uppercase tracking-widest text-white/30">
                                    powered by cal.com &middot; 100% free
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
